import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import type { PriceIntelligence } from '../types';

interface PriceEstimatorProps {
  category: string;
  attributes: Record<string, string>;
  onPriceSelect: (price: number) => void;
  selectedPrice?: number;
}

export function PriceEstimator({
  category,
  attributes,
  onPriceSelect,
  selectedPrice,
}: PriceEstimatorProps): JSX.Element {
  const [intelligence, setIntelligence] = useState<PriceIntelligence | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!category) return;

    const fetchIntelligence = async () => {
      setIsLoading(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const mockData: PriceIntelligence = {
        estimatedRange: {
          min: Math.floor(Math.random() * 5000) + 10000,
          max: Math.floor(Math.random() * 10000) + 25000,
          optimal: Math.floor(Math.random() * 5000) + 18000,
        },
        confidence: Math.floor(Math.random() * 30) + 70,
        marketTrend: ['rising', 'stable', 'falling'][Math.floor(Math.random() * 3)] as PriceIntelligence['marketTrend'],
        comparables: [
          { id: '1', title: 'Similar item A', price: 18500, condition: 'Good', location: 'Lahore', date: new Date() },
          { id: '2', title: 'Similar item B', price: 22000, condition: 'Excellent', location: 'Karachi', date: new Date() },
          { id: '3', title: 'Similar item C', price: 16500, condition: 'Fair', location: 'Islamabad', date: new Date() },
        ],
        demandLevel: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as PriceIntelligence['demandLevel'],
      };

      setIntelligence(mockData);
      if (!selectedPrice) {
        onPriceSelect(mockData.estimatedRange.optimal);
      }
      setIsLoading(false);
    };

    fetchIntelligence();
  }, [category, attributes, onPriceSelect, selectedPrice]);

  const chartData = intelligence?.comparables.map((c) => ({
    name: c.title.slice(0, 10) + '...',
    price: c.price,
    yours: selectedPrice,
  })) || [];

  const getTrendIcon = () => {
    if (!intelligence) return null;
    switch (intelligence.marketTrend) {
      case 'rising':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'falling':
        return <TrendingDown className="w-4 h-4 text-danger" />;
      default:
        return <Minus className="w-4 h-4 text-dark-400" />;
    }
  };

  const getTrendText = () => {
    if (!intelligence) return '';
    switch (intelligence.marketTrend) {
      case 'rising':
        return 'Prices trending up';
      case 'falling':
        return 'Prices trending down';
      default:
        return 'Stable market';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-dark-50 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-dark-400">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="w-5 h-5" />
          </motion.div>
          <span className="text-sm">Analyzing market prices...</span>
        </div>
        <div className="h-32 bg-dark-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!intelligence) {
    return (
      <div className="bg-dark-50 rounded-xl p-6">
        <div className="h-32 bg-dark-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Price range card */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl p-5 text-white">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-primary-100 text-sm">AI Recommended Price</p>
            <p className="text-3xl font-bold mt-1">
              PKR {intelligence.estimatedRange.optimal.toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
            {getTrendIcon()}
            <span className="text-xs font-medium">{getTrendText()}</span>
          </div>
        </div>

        {/* Price range slider visualization */}
        <div className="relative h-2 bg-white/20 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-white/40 rounded-full"
            style={{
              left: '20%',
              right: '20%',
            }}
          />
          <motion.div
            className="absolute h-full w-3 bg-white rounded-full shadow-lg -ml-1.5"
            initial={{ left: '50%' }}
            animate={{
              left: selectedPrice
                ? `${((selectedPrice - intelligence.estimatedRange.min) / (intelligence.estimatedRange.max - intelligence.estimatedRange.min)) * 60 + 20}%`
                : '50%',
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-primary-100 mt-2">
          <span>Min: PKR {intelligence.estimatedRange.min.toLocaleString()}</span>
          <span>Max: PKR {intelligence.estimatedRange.max.toLocaleString()}</span>
        </div>
      </div>

      {/* Price input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-dark-400 font-medium">PKR</span>
        </div>
        <input
          type="number"
          value={selectedPrice || ''}
          onChange={(e) => onPriceSelect(Number(e.target.value))}
          className="w-full pl-12 pr-4 py-3 border border-dark-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg font-semibold"
          placeholder="Enter price"
        />
      </div>

      {/* Market insights */}
      <div className="bg-dark-50 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-dark-700">Market Confidence</span>
          <span className="text-sm font-bold text-primary-600">{intelligence.confidence}%</span>
        </div>
        <div className="h-2 bg-dark-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${intelligence.confidence}%` }}
            transition={{ duration: 1 }}
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-dark-500">
          <Info className="w-3 h-3" />
          <span>Based on {intelligence.comparables.length * 15} similar listings in the last 30 days</span>
        </div>
      </div>

      {/* Comparable listings */}
      <div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center justify-between w-full text-sm font-medium text-dark-700 hover:text-primary-600 transition-colors"
        >
          Similar Listings
          <motion.span animate={{ rotate: showDetails ? 180 : 0 }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.span>
        </button>

        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 space-y-2">
                {intelligence.comparables.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-dark-100"
                  >
                    <div>
                      <p className="text-sm font-medium text-dark-900">{item.title}</p>
                      <p className="text-xs text-dark-500">
                        {item.condition} • {item.location}
                      </p>
                    </div>
                    <span className="font-semibold text-dark-700">
                      PKR {item.price.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Mini chart */}
              <div className="mt-4 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" hide />
                    <YAxis hide />
                    <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.price === selectedPrice ? '#3b6fff' : '#e2e8f0'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
